import { useEffect, useState } from "react";
import api, { AU_STATES, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

const ROLES = ["super_admin", "management", "general_manager", "sales_agent", "customer_service"];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`); toast.success("Deleted"); load();
  };
  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">User Management</h1><p className="text-slate-600 mt-1">{users.length} users</p></div>
        <button onClick={() => setShowNew(true)} className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" data-testid="new-user-btn"><Plus className="w-4 h-4" /> New User</button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">States</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>{users.map(u => (
            <tr key={u.id} className="border-t hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold">{u.name}</td>
              <td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3">{ROLE_LABELS[u.role] || u.role}</td>
              <td className="px-4 py-3 text-xs">{(u.assigned_states || []).join(", ") || "—"}</td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => del(u.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {showNew && <NewUser onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewUser({ onClose, onSaved }) {
  const [f, setF] = useState({ email: "", name: "", role: "sales_agent", password: "", assigned_states: [] });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/users", f); toast.success("User created"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">New User</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-4">
          <F label="Full Name *"><input required value={f.name} onChange={upd("name")} className="w-full h-11 px-3 border rounded-lg" data-testid="user-name" /></F>
          <F label="Email *"><input required type="email" value={f.email} onChange={upd("email")} className="w-full h-11 px-3 border rounded-lg" data-testid="user-email" /></F>
          <F label="Password *"><input required type="password" value={f.password} onChange={upd("password")} className="w-full h-11 px-3 border rounded-lg" data-testid="user-password" /></F>
          <F label="Role"><select value={f.role} onChange={upd("role")} className="w-full h-11 px-3 border rounded-lg" data-testid="user-role">{ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></F>
          <F label="Assigned States">
            <div className="flex flex-wrap gap-2">
              {AU_STATES.map(s => (
                <label key={s} className={`px-3 py-1 rounded-full border text-xs cursor-pointer ${f.assigned_states.includes(s) ? "bg-[#001F54] text-white border-[#001F54]" : "border-slate-300"}`}>
                  <input type="checkbox" className="hidden" checked={f.assigned_states.includes(s)} onChange={() => setF({ ...f, assigned_states: f.assigned_states.includes(s) ? f.assigned_states.filter(x => x !== s) : [...f.assigned_states, s] })} />
                  {s}
                </label>
              ))}
            </div>
          </F>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-user-btn">Save</button></div>
      </form>
    </div>
  );
}
const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
